import { DOCUMENT, NgTemplateOutlet } from '@angular/common'
import {
  AfterContentInit,
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  NgZone,
  numberAttribute,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  input,
  inject,
  output,
  signal,
} from '@angular/core'
import { Message, MessageService, PrimeNGConfig, PrimeTemplate } from '../api'

import { ObjectUtils, UniqueComponentId, ZIndexUtils } from '../utils'
import { Subscription } from 'rxjs'
import { ToastCloseEvent, ToastItemCloseEvent, ToastPositionType } from './toast.interface'
import { DomHandler } from '../dom'
import {
  CheckIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon,
  TimesCircleIcon,
  TimesIcon,
} from '@shared/modules/primeng-clone/icons'

import { Ripple } from '../ripple/ripple'

/** Reads the leading duration (e.g. "250ms" or ".1s") off a CSS transition string. */
function parseDurationMs(transition: string): number {
  const match = /^\s*([\d.]+)(ms|s)/.exec(transition)
  if (!match) {
    return 300
  }
  const value = parseFloat(match[1])
  return match[2] === 's' ? value * 1000 : value
}

@Component({
  selector: 'p-toastItem',
  template: `
    <div
      #container
      [attr.id]="message()?.id"
      [class]="
        [
          message()?.styleClass,
          'p-toast-message-' + message()?.severity,
          'p-toast-message',
          messageEntered() ? 'p-toast-message-entered' : '',
          leaving() ? 'p-toast-message-leave-active' : '',
        ].join(' ')
      "
      [style.--toast-show-transition]="showTransitionOptions()"
      [style.--toast-hide-transition]="hideTransitionOptions()"
      [style.--toast-show-transform]="showTransformOptions()"
      [style.--toast-hide-transform]="hideTransformOptions()"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      [attr.data-pc-name]="'toast'"
      [attr.data-pc-section]="'root'"
    >
      @if (headlessTemplate()) {
        <ng-container
          *ngTemplateOutlet="
            headlessTemplate();
            context: { $implicit: message(), closeFn: onCloseIconClick }
          "
        />
      } @else {
        <div
          class="p-toast-message-content"
          [class]="message()?.contentStyleClass"
          [attr.data-pc-section]="'content'"
        >
          @if (!template()) {
            @if (message().icon) {
              <span [class]="'p-toast-message-icon pi ' + message().icon"></span>
            }
            @if (!message().icon) {
              <span
                class="p-toast-message-icon"
                [attr.aria-hidden]="true"
                [attr.data-pc-section]="'icon'"
              >
                <ng-container>
                  @if (message().severity === 'success') {
                    <CheckIcon [attr.aria-hidden]="true" [attr.data-pc-section]="'icon'" />
                  }
                  @if (message().severity === 'info') {
                    <InfoCircleIcon [attr.aria-hidden]="true" [attr.data-pc-section]="'icon'" />
                  }
                  @if (message().severity === 'error') {
                    <TimesCircleIcon [attr.aria-hidden]="true" [attr.data-pc-section]="'icon'" />
                  }
                  @if (message().severity === 'warn') {
                    <ExclamationTriangleIcon
                      [attr.aria-hidden]="true"
                      [attr.data-pc-section]="'icon'"
                    />
                  }
                </ng-container>
              </span>
            }
            <div class="p-toast-message-text" [attr.data-pc-section]="'text'">
              <div class="p-toast-summary" [attr.data-pc-section]="'summary'">
                {{ message().summary }}
              </div>
              <div class="p-toast-detail" [attr.data-pc-section]="'detail'">
                {{ message().detail }}
              </div>
            </div>
          }
          <ng-container *ngTemplateOutlet="template(); context: { $implicit: message() }" />
          @if (message()?.closable !== false) {
            <button
              type="button"
              class="p-toast-icon-close p-link"
              (click)="onCloseIconClick($event)"
              (keydown.enter)="onCloseIconClick($event)"
              pRipple
              [attr.aria-label]="closeAriaLabel"
              [attr.data-pc-section]="'closebutton'"
            >
              @if (message().closeIcon) {
                <span
                  [class]="'pt-1 text-base p-toast-message-icon pi ' + message().closeIcon"
                ></span>
              }
              @if (!message().closeIcon) {
                <TimesIcon
                  [styleClass]="'p-toast-icon-close-icon'"
                  [attr.aria-hidden]="true"
                  [attr.data-pc-section]="'closeicon'"
                />
              }
            </button>
          }
        </div>
      }
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'p-element',
  },
  imports: [
    NgTemplateOutlet,
    CheckIcon,
    InfoCircleIcon,
    TimesCircleIcon,
    ExclamationTriangleIcon,
    Ripple,
    TimesIcon,
  ],
})
export class ToastItem implements AfterViewInit, OnDestroy {
  private zone = inject(NgZone)
  private config = inject(PrimeNGConfig)

  readonly message = input<Message | null | undefined>(undefined)

  readonly index = input<number | null | undefined, unknown>(undefined, {
    transform: numberAttribute,
  })

  readonly life = input<number, unknown>(undefined, { transform: numberAttribute })

  readonly template = input<TemplateRef<any> | undefined>(undefined)

  readonly headlessTemplate = input<TemplateRef<any> | undefined>(undefined)

  readonly showTransformOptions = input<string | undefined>(undefined)

  readonly hideTransformOptions = input<string | undefined>(undefined)

  readonly showTransitionOptions = input<string | undefined>(undefined)

  readonly hideTransitionOptions = input<string | undefined>(undefined)

  readonly onClose = output<ToastItemCloseEvent>()

  @ViewChild('container') containerViewChild: ElementRef | undefined

  timeout: any

  closeTimeout: ReturnType<typeof setTimeout> | null = null

  /**
   * Starts `false` on mount so the enter transition has a genuine "before" class state to
   * animate from; flipped to `true` on the next macrotask, guaranteeing a real paint happens
   * in between (the classic, browser-agnostic CSS-enter-transition trick).
   */
  messageEntered = signal(false)

  /**
   * `true` while the leave transition plays. Actual removal from the parent's `messages` array
   * (via onClose) is deliberately delayed until the transition finishes - see beginClose().
   */
  leaving = signal(false)

  ngAfterViewInit() {
    setTimeout(() => this.messageEntered.set(true))
    this.initTimeout()
  }

  initTimeout() {
    if (!this.message()?.sticky) {
      this.zone.runOutsideAngular(() => {
        this.timeout = setTimeout(
          () => this.beginClose(),
          this.message()?.life || this.life() || 3000,
        )
      })
    }
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
  }

  onMouseEnter() {
    this.clearTimeout()
  }

  onMouseLeave() {
    this.initTimeout()
  }

  beginClose() {
    if (this.leaving()) {
      return
    }

    this.clearTimeout()
    this.leaving.set(true)

    this.closeTimeout = setTimeout(
      () => {
        this.onClose.emit({
          index: this.index() as number,
          message: this.message() as Message,
        })
      },
      parseDurationMs(this.hideTransitionOptions() || '250ms ease-in'),
    )
  }

  onCloseIconClick = (event: Event) => {
    this.beginClose()
    event.preventDefault()
  }

  get closeAriaLabel() {
    return this.config.translation.aria ? this.config.translation.aria.close : undefined
  }

  ngOnDestroy() {
    this.clearTimeout()
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
    }
  }
}

/**
 * Toast is used to display messages in an overlay.
 * @group Components
 */
@Component({
  selector: 'p-toast',
  template: `
    <div
      #container
      class="p-toast p-component"
      [style]="style()"
      [class]="[styleClass(), 'p-toast-' + position()].join(' ')"
    >
      @for (msg of messages; track msg; let i = $index) {
        <p-toastItem
          [message]="msg"
          [index]="i"
          [life]="life()"
          (onClose)="onMessageClose($event)"
          [template]="template"
          [headlessTemplate]="headlessTemplate"
          [showTransformOptions]="showTransformOptions()"
          [hideTransformOptions]="hideTransformOptions()"
          [showTransitionOptions]="showTransitionOptions()"
          [hideTransitionOptions]="hideTransitionOptions()"
        />
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./toast.css'],
  host: {
    class: 'p-element',
  },
  imports: [ToastItem],
})
export class Toast implements OnInit, AfterContentInit, OnDestroy, AfterViewInit {
  private document = inject<Document>(DOCUMENT)
  private renderer = inject(Renderer2)
  messageService = inject(MessageService)
  private cd = inject(ChangeDetectorRef)
  config = inject(PrimeNGConfig)

  /**
   * Key of the message in case message is targeted to a specific toast component.
   * @group Props
   */
  readonly key = input<
    string | undefined
    /**
     * Whether to automatically manage layering.
     * @group Props
     */
  >(undefined)
  /**
   * Whether to automatically manage layering.
   * @group Props
   */
  readonly autoZIndex = input<boolean, unknown>(
    true,
    /**
     * Base zIndex value to use in layering.
     * @group Props
     */
    { transform: booleanAttribute },
  )
  /**
   * Base zIndex value to use in layering.
   * @group Props
   */
  readonly baseZIndex = input<number, unknown>(
    0,
    /**
     * The default time to display messages for in milliseconds.
     * @group Props
     */
    { transform: numberAttribute },
  )
  /**
   * The default time to display messages for in milliseconds.
   * @group Props
   */
  readonly life = input<number, unknown>(
    3000,
    /**
     * Inline style of the component.
     * @group Props
     */
    { transform: numberAttribute },
  )
  /**
   * Inline style of the component.
   * @group Props
   */
  readonly style = input<
    Record<string, any> | null | undefined
    /**
     * Inline class of the component.
     * @group Props
     */
  >(undefined)
  /**
   * Inline class of the component.
   * @group Props
   */
  readonly styleClass = input<
    string | undefined
    /**
     * Position of the toast in viewport.
     * @group Props
     */
  >(undefined)

  /**
   * Position of the toast in viewport.
   * @group Props
   */
  readonly position = input<ToastPositionType>('top-right')

  /**
   * It does not add the new message if there is already a toast displayed with the same content
   * @group Props
   */
  readonly preventOpenDuplicates = input<boolean, unknown>(
    false,
    /**
     * Displays only once a message with the same content.
     * @group Props
     */
    { transform: booleanAttribute },
  )
  /**
   * Displays only once a message with the same content.
   * @group Props
   */
  readonly preventDuplicates = input<boolean, unknown>(
    false,
    /**
     * Transform options of the show animation.
     * @group Props
     */
    { transform: booleanAttribute },
  )
  /**
   * Transform options of the show animation.
   * @group Props
   */
  readonly showTransformOptions = input<string>(
    'translateY(100%)',
    /**
     * Transform options of the hide animation.
     * @group Props
     */
  )
  /**
   * Transform options of the hide animation.
   * @group Props
   */
  readonly hideTransformOptions = input<string>(
    'translateY(-100%)',
    /**
     * Transition options of the show animation.
     * @group Props
     */
  )
  /**
   * Transition options of the show animation.
   * @group Props
   */
  readonly showTransitionOptions = input<string>(
    '300ms ease-out',
    /**
     * Transition options of the hide animation.
     * @group Props
     */
  )
  /**
   * Transition options of the hide animation.
   * @group Props
   */
  readonly hideTransitionOptions = input<string>(
    '250ms ease-in',
    /**
     * Object literal to define styles per screen size.
     * @group Props
     */
  )
  /**
   * Object literal to define styles per screen size.
   * @group Props
   */
  readonly breakpoints = input<
    Record<string, any> | undefined
    /**
     * Callback to invoke when a message is closed.
     * @param {ToastCloseEvent} event - custom close event.
     * @group Emits
     */
  >(undefined)
  /**
   * Callback to invoke when a message is closed.
   * @param {ToastCloseEvent} event - custom close event.
   * @group Emits
   */
  readonly onClose = output<ToastCloseEvent>()

  @ViewChild('container') containerViewChild: ElementRef | undefined

  @ContentChildren(PrimeTemplate) templates: QueryList<PrimeTemplate> | undefined

  messageSubscription: Subscription | undefined

  clearSubscription: Subscription | undefined

  messages: Message[] | null | undefined

  messagesArchieve: Message[] | undefined

  template: TemplateRef<any> | undefined

  headlessTemplate: TemplateRef<any> | undefined

  styleElement: any

  id: string = UniqueComponentId()

  ngOnInit() {
    this.messageSubscription = this.messageService.messageObserver.subscribe((messages) => {
      if (messages) {
        if (Array.isArray(messages)) {
          const filteredMessages = messages.filter((m) => this.canAdd(m))
          this.add(filteredMessages)
        } else if (this.canAdd(messages)) {
          this.add([messages])
        }
      }
    })

    this.clearSubscription = this.messageService.clearObserver.subscribe((key) => {
      if (key) {
        if (this.key() === key) {
          this.messages = null
        }
      } else {
        this.messages = null
      }

      this.cd.markForCheck()
    })
  }

  ngAfterViewInit() {
    if (this.breakpoints()) {
      this.createStyle()
    }
  }

  add(messages: Message[]): void {
    this.messages = this.messages ? [...this.messages, ...messages] : [...messages]

    if (this.preventDuplicates()) {
      this.messagesArchieve = this.messagesArchieve
        ? [...this.messagesArchieve, ...messages]
        : [...messages]
    }

    this.renderer.setAttribute(this.containerViewChild?.nativeElement, this.id, '')
    if (this.autoZIndex() && this.containerViewChild?.nativeElement.style.zIndex === '') {
      ZIndexUtils.set(
        'modal',
        this.containerViewChild?.nativeElement,
        this.baseZIndex() || this.config.zIndex.modal,
      )
    }

    this.cd.markForCheck()
  }

  canAdd(message: Message): boolean {
    let allow = this.key() === message.key

    if (allow && this.preventOpenDuplicates()) {
      allow = !this.containsMessage(this.messages!, message)
    }

    if (allow && this.preventDuplicates()) {
      allow = !this.containsMessage(this.messagesArchieve!, message)
    }

    return allow
  }

  containsMessage(collection: Message[], message: Message): boolean {
    if (!collection) {
      return false
    }

    return (
      collection.find((m) => {
        return (
          m.summary === message.summary &&
          m.detail == message.detail &&
          m.severity === message.severity
        )
      }) != null
    )
  }

  ngAfterContentInit() {
    this.templates?.forEach((item) => {
      switch (item.getType()) {
        case 'message':
          this.template = item.template
          break
        case 'headless':
          this.headlessTemplate = item.template
          break

        default:
          this.template = item.template
          break
      }
    })
  }

  onMessageClose(event: ToastItemCloseEvent) {
    this.messages?.splice(event.index, 1)

    if (this.autoZIndex() && ObjectUtils.isEmpty(this.messages)) {
      ZIndexUtils.clear(this.containerViewChild?.nativeElement)
    }

    this.onClose.emit({
      message: event.message,
    })

    this.cd.detectChanges()
  }

  createStyle() {
    if (!this.styleElement) {
      this.styleElement = this.renderer.createElement('style')
      this.styleElement.type = 'text/css'
      DomHandler.setAttribute(this.styleElement, 'nonce', this.config?.csp()?.nonce)
      this.renderer.appendChild(this.document.head, this.styleElement)
      let innerHTML = ''
      for (const breakpoint in this.breakpoints()) {
        let breakpointStyle = ''
        for (const styleProp in this.breakpoints()[breakpoint]) {
          breakpointStyle +=
            styleProp + ':' + this.breakpoints()[breakpoint][styleProp] + ' !important;'
        }
        innerHTML += `
                    @media screen and (max-width: ${breakpoint}) {
                        .p-toast[${this.id}] {
                           ${breakpointStyle}
                        }
                    }
                `
      }

      this.renderer.setProperty(this.styleElement, 'innerHTML', innerHTML)
    }
  }

  destroyStyle() {
    if (this.styleElement) {
      this.renderer.removeChild(this.document.head, this.styleElement)
      this.styleElement = null
    }
  }

  ngOnDestroy() {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe()
    }

    if (this.containerViewChild && this.autoZIndex()) {
      ZIndexUtils.clear(this.containerViewChild.nativeElement)
    }

    if (this.clearSubscription) {
      this.clearSubscription.unsubscribe()
    }

    this.destroyStyle()
  }
}
